import { useState } from "react";
import { Box } from "@chakra-ui/react";

import { Polaroid } from "#app/components/Polaroid.jsx";

// Asymmetric hero mosaic. One large polaroid anchors the composition; up to
// four smaller satellites surround it. Each slot uses a fixed rotation and
// aspect ratio so the layout looks scattered, not gridded.
const SLOTS = [
  { area: "hero", rotate: "-1.6deg", ratio: "4/3" },
  { area: "a", rotate: "2.2deg", ratio: "3/2" },
  { area: "b", rotate: "-2.4deg", ratio: "3/4" },
  { area: "c", rotate: "1.8deg", ratio: "1/1" },
  { area: "d", rotate: "-1.2deg", ratio: "4/3" },
];

const TEMPLATES = {
  3: {
    md: {
      columns: "1.6fr 1fr",
      areas: `
        "hero a"
        "hero b"
      `,
    },
    base: {
      columns: "1fr 1fr",
      areas: `
        "hero hero"
        "a    b"
      `,
    },
  },
  4: {
    md: {
      columns: "1.6fr 1fr 1fr",
      areas: `
        "hero a b"
        "hero c b"
      `,
    },
    base: {
      columns: "1fr 1fr",
      areas: `
        "hero hero"
        "a    b"
        "c    c"
      `,
    },
  },
  5: {
    md: {
      columns: "1.6fr 1fr 1fr",
      areas: `
        "hero a b"
        "hero c d"
      `,
    },
    base: {
      columns: "1fr 1fr",
      areas: `
        "hero hero"
        "a    b"
        "c    d"
      `,
    },
  },
};

/**
 * Asymmetric polaroid mosaic for facility landing pages.
 *
 * One hero polaroid plus up to four satellites in a CSS grid composition.
 * Photos come from facility.extraAttributes.photos (Campspot park images);
 * the first photo always becomes the hero so callers can curate ordering.
 *
 * @param {object} props
 * @param {string[]} props.photos - Array of image URLs.
 * @param {string} props.facilityName - Used for alt text.
 */
export function PhotoGallery({ photos, facilityName }) {
  const [failedUrls, setFailedUrls] = useState(new Set());

  if (!photos || photos.length === 0) return null;

  const visible = photos.filter((url) => !failedUrls.has(url)).slice(0, 5);
  if (visible.length === 0) return null;

  // Below 3 photos there isn't enough variety for a real composition;
  // render a simple centered hero so the section still has presence.
  const count = visible.length >= 3 ? Math.min(visible.length, 5) : 1;
  const template = TEMPLATES[count];

  if (!template) {
    return (
      <Box
        w="100%"
        maxW="container.sm"
        mx="auto"
        px={{ base: 6, md: 0 }}
        py={{ base: 4, md: 8 }}
        role="region"
        aria-label={`${facilityName} photos`}
      >
        <Polaroid
          src={visible[0]}
          alt={facilityName}
          rotate={SLOTS[0].rotate}
          ratio={SLOTS[0].ratio}
          onError={() => setFailedUrls((prev) => new Set(prev).add(visible[0]))}
        />
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      maxW="container.lg"
      mx="auto"
      px={{ base: 6, md: 4 }}
      py={{ base: 4, md: 8 }}
      role="region"
      aria-label={`${facilityName} photos`}
    >
      <Box
        display="grid"
        gap={{ base: 5, md: 8 }}
        alignItems="center"
        gridTemplateColumns={{
          base: template.base.columns,
          md: template.md.columns,
        }}
        gridTemplateAreas={{ base: template.base.areas, md: template.md.areas }}
      >
        {visible.slice(0, count).map((url, i) => {
          const slot = SLOTS[i];
          return (
            <Box key={url} gridArea={slot.area}>
              <Polaroid
                src={url}
                alt={
                  i === 0
                    ? facilityName
                    : `${facilityName} photo ${i + 1} of ${count}`
                }
                rotate={slot.rotate}
                ratio={slot.ratio}
                onError={() => setFailedUrls((prev) => new Set(prev).add(url))}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
